/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';

import { Button, Grid, typography2 } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import ActionBar from 'bundles/expression-of-interest/components/ActionBar';
import { EoiDialog } from 'bundles/expression-of-interest/components/EoiDialog';
import { EoiOptimizationsContextProvider } from 'bundles/expression-of-interest/components/EoiOptimizationsContext';
import type { ActionBarProps, EoiButtonType } from 'bundles/expression-of-interest/types';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { ProductTypes } from 'bundles/university-program-qualification/utils/constants';

import _t from 'i18n!nls/xddp';

type Props = {
  slug: string;
  degreeId: string;
  applicationUrl?: string;
  programInterestText?: string;
  enrollButtonData?: EoiButtonType | null;
  emailMeInfoButtonData?: EoiButtonType;
  actionBarData: ActionBarProps;
  isEnrollFlow?: boolean;
  isDegree?: boolean;
};

const styles = {
  container: css`
    background-color: var(--cds-color-neutral-primary-invert);
    color: var(--cds-color-neutral-primary);
    box-shadow: 0 2px 4px 0 var(--cds-color-neutral-primary);
    border-top: 12px solid var(--cds-color-callouts-background-primary);
    position: relative;
    z-index: 5;
    width: 100%;
    padding: var(--cds-spacing-300);

    .rc-Markdown.styled {
      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      p {
        font-family: ${typography2.bodyPrimary};
      }

      a {
        &:hover,
        &:focus {
          text-decoration: none;
        }

        &:visited {
          color: var(--cds-color-interactive-primary-pressed);
        }
      }

      h2 {
        ${typography2.subtitleLarge};
      }

      p {
        ${typography2.bodyPrimary};
        margin: 0;
      }
    }
  `,

  content: () => css`
    margin-top: var(--cds-spacing-200);
    width: 100%;
  `,

  button: css`
    margin-bottom: var(--cds-spacing-100);
  `,
};

export const EoiWidgetContainer: React.FunctionComponent<Props> = (props) => {
  const {
    slug,
    degreeId,
    applicationUrl,
    programInterestText,
    actionBarData,
    enrollButtonData,
    emailMeInfoButtonData,
    isEnrollFlow,
    isDegree,
  } = props;

  const [isEoiOpen, setIsEoiOpen] = useState<boolean>(false);

  const programInterestTitle = _t('Learn more about this program');
  let programInterestDescription = '';
  if (!applicationUrl) {
    programInterestDescription = _t('Request more info about this program');
  } else {
    programInterestDescription = _t('Request more info and start your application');
  }

  const programInterestDefault = `## ${programInterestTitle}\n${programInterestDescription}`;

  const handleEmailMeInfoClick = () => {
    setIsEoiOpen(true);
    emailMeInfoButtonData?.doAction?.();
  };

  const handleEnrollButtonClick = () => {
    enrollButtonData?.doAction?.();

    if (enrollButtonData?.isInChecklistMode) {
      setIsEoiOpen(true);
    }
  };

  return (
    <Grid container direction="column" justifyContent="flex-start" alignItems="flex-start" css={styles.container}>
      <ActionBar actionBarData={actionBarData}>
        <Grid
          item
          xs={12}
          md={6}
          lg={4}
          container
          direction="row-reverse"
          alignItems="flex-start"
          justifyContent="flex-start"
          spacing={8}
        >
          <Grid item xs={6} md={5}>
            {(applicationUrl || isEnrollFlow) && (
              <Button
                onClick={handleEnrollButtonClick}
                fullWidth
                component={TrackedLink2}
                href={enrollButtonData?.buttonOptions?.htmlAttributes?.href}
                target={enrollButtonData?.buttonOptions?.htmlAttributes?.target}
                rel={enrollButtonData?.buttonOptions?.htmlAttributes?.rel}
                trackingName={enrollButtonData?.buttonOptions?.trackingName}
                withVisibilityTracking={false}
                requireFullyVisible={false}
              >
                {enrollButtonData?.buttonOptions?.label || _t('Enroll')}
              </Button>
            )}
          </Grid>
          <Grid item xs={6} md={5}>
            <Button
              onClick={handleEmailMeInfoClick}
              fullWidth
              component={TrackedLink2}
              trackingName="email_me_info"
              withVisibilityTracking={false}
              requireFullyVisible={false}
              variant="secondary"
            >
              {emailMeInfoButtonData?.buttonOptions?.label || _t('Email me info')}
            </Button>
          </Grid>
        </Grid>
      </ActionBar>

      <Grid item>
        <Markdown source={programInterestText || programInterestDefault} />
      </Grid>

      <Grid item css={styles.content}>
        {(applicationUrl || isEnrollFlow) && (
          <Button
            onClick={handleEnrollButtonClick}
            fullWidth
            component={TrackedLink2}
            href={enrollButtonData?.buttonOptions?.htmlAttributes?.href}
            target={enrollButtonData?.buttonOptions?.htmlAttributes?.target}
            rel={enrollButtonData?.buttonOptions?.htmlAttributes?.rel}
            trackingName={enrollButtonData?.buttonOptions?.trackingName}
            withVisibilityTracking={false}
            requireFullyVisible={false}
            css={styles.button}
            data-testid="apply-button"
          >
            {enrollButtonData?.buttonOptions?.label || _t('Enroll')}
          </Button>
        )}
        <Button
          onClick={handleEmailMeInfoClick}
          fullWidth
          component={TrackedLink2}
          trackingName="email_me_info"
          withVisibilityTracking={false}
          requireFullyVisible={false}
          variant="secondary"
          css={styles.button}
        >
          {emailMeInfoButtonData?.buttonOptions?.label || _t('Email me info')}
        </Button>

        <EoiOptimizationsContextProvider
          slug={slug}
          productType={isDegree ? ProductTypes.DEGREE : ProductTypes.SPECIALIZATION}
          applyButtonData={enrollButtonData}
        >
          <EoiDialog
            onOpen={() => setIsEoiOpen(true)}
            isOpen={isEoiOpen}
            onClose={() => setIsEoiOpen(false)}
            productId={tupleToStringKey([ProductTypes.DEGREE, degreeId])}
            applicationUrl={applicationUrl}
          />
        </EoiOptimizationsContextProvider>
      </Grid>
    </Grid>
  );
};

export default EoiWidgetContainer;
